import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setUser, setError, fetchUserRoles } from '../../redux/authSlice';
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";

const LoginPage = () => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const error = useSelector((state) => state.auth.error);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("logout") === "true") {
      dispatch(setError("Вы успешно вышли из системы."));
    }
  }, [location, dispatch]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await fetch('/api/perform_login', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorContent = await response.json();
        dispatch(setError(errorContent.message || 'Ошибка входа'));
        return;
      }

      const userData = await response.json();

      // Роли и привилегии всегда подтягиваем с /api/users/profile
      dispatch(setUser({ user: userData.user }));
      await dispatch(fetchUserRoles());

      navigate('/'); // Переход на главную страницу
    } catch (error) {
      dispatch(setError('Ошибка сети. Попробуйте еще раз.'));
    } finally {
      setLoading(false);
    }
  };

  const isLogoutMessage = error === "Вы успешно вышли из системы.";

  return (
      <div className="flex min-h-[80vh] items-center justify-center bg-neutral-50 px-4 py-16">
        <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 shadow-[0px_1px_1px_rgba(0,0,0,0.03),0px_5px_15px_rgba(0,0,0,0.08)]">
          <div className="mb-6 flex justify-center">
            <img src="/images/lksm.png" alt="ЛКСМ РФ" className="h-14 w-auto rounded-xl bg-white p-1 shadow" />
          </div>

          <h2 className="mb-8 text-center text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-rose-500 to-amber-500">
            Вход
          </h2>

          {error && (
              <div
                  className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
                      isLogoutMessage
                          ? "border-green-200 bg-green-50 text-green-700"
                          : "border-red-200 bg-red-50 text-red-700"
                  }`}
              >
                {error}
              </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <div className="mt-1.5">
                <Input
                    id="email"
                    type="email"
                    placeholder="name@example.ru"
                    {...register("email", { required: "E-mail обязателен" })}
                />
              </div>
              {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password">Пароль</Label>
              <div className="mt-1.5">
                <Input
                    id="password"
                    type={visible ? "text" : "password"}
                    placeholder="Пароль"
                    className={errors.password ? "focus-visible:ring-red-500" : ""}
                    {...register("password", { required: "Пароль обязателен" })}
                />
              </div>
              {errors.password && (
                  <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center gap-6">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-600">
                <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded accent-red-600"
                    onClick={() => setVisible(!visible)}
                />
                Показать пароль
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-600">
                <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded accent-red-600"
                    {...register("remember-me")}
                />
                Запомнить меня
              </label>
            </div>

            <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Вход..." : "Войти"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-neutral-500">
            Ещё не зарегистрированы?{" "}
            <a href="/registration" className="font-medium text-red-600 hover:underline">
              Зарегистрироваться
            </a>
          </p>
        </div>
      </div>
  );
};

export default LoginPage;